<template>
  <div>

    <div class="mb-2 position-relative">
      <input v-model="search" class="input form-control" placeholder="Buscar permissão...">
      <search-icon class="icon icon-within"/>
    </div>

    <p v-if="products.length === 0" class="text-center text-muted py-4 mb-0">
      <template v-if="searching">Nenhuma permissão encontrada para "{{ search }}"</template>
      <template v-else>Nenhum produto contratado nesta conta.</template>
    </p>

    <!-- Nível 1: produto -->
    <div v-for="product in products" :key="product.app" class="card mb-2">

      <div class="card-header d-flex align-items-start justify-content-between">
        <span class="pointer flex-grow-1" @click="toggleOpen(product.app)">
          <chevron-down-icon v-if="isOpen(product.app)" class="icon-sm"/>
          <chevron-right-icon v-else class="icon-sm"/>
          <strong>{{ product.label }}</strong>
          <span class="badge badge-light ml-2">{{ marked(productIds(product)) }}/{{ productIds(product).length }}</span>
        </span>
        <span v-if="!disabled" class="text-nowrap">
          <a class="small pointer" @click="toggleMany(productIds(product), true)">Tudo</a>
          <span class="text-muted mx-1">|</span>
          <a class="small text-muted pointer" @click="toggleMany(productIds(product), false)">Nada</a>
        </span>
      </div>

      <div v-if="isOpen(product.app)" class="card-body p-0">

        <!-- Nível 2: aba -->
        <div v-for="section in product.sections" :key="section.key" class="border-bottom">

          <div class="section-header d-flex align-items-start justify-content-between">
            <span class="pointer flex-grow-1" @click="toggleOpen(product.app + '/' + section.key)">
              <chevron-down-icon v-if="isOpen(product.app + '/' + section.key)" class="icon-sm"/>
              <chevron-right-icon v-else class="icon-sm"/>
              <span class="text-uppercase small text-muted">{{ section.label }}</span>
              <span class="badge badge-light ml-2">
                {{ marked(ids(product.app, items(section))) }}/{{ ids(product.app, items(section)).length }}
              </span>
              <small v-if="section.hint" class="d-block text-muted">{{ section.hint }}</small>
            </span>
            <span v-if="!disabled" class="text-nowrap">
              <a class="small pointer" @click="toggleMany(ids(product.app, items(section)), true)">Tudo</a>
              <span class="text-muted mx-1">|</span>
              <a class="small text-muted pointer" @click="toggleMany(ids(product.app, items(section)), false)">Nada</a>
            </span>
          </div>

          <template v-if="isOpen(product.app + '/' + section.key)">

            <permission-item-list
                v-if="section.items && section.items.length"
                :app="product.app"
                :dimensions="catalog.scope_dimensions"
                :disabled="disabled"
                :items="section.items"
                :scopes="scopes"
                :section="section.key"
                :selected="selected"
                @toggle="onToggle"
                @scope-clear="onScopeClear"
                @scope-toggle="onScopeToggle">
              <template #companies="{ dimension }">
                <company-scope
                    :all-companies="allCompanies"
                    :disabled="disabled"
                    :hint="dimension.hint"
                    :label="dimension.label"
                    :selected-cnpjs="cnpjs"
                    @all-companies="$emit('all-companies', $event)"
                    @cnpjs="$emit('cnpjs', $event)"/>
              </template>
            </permission-item-list>

            <!-- Nível 3: sub-aba -->
            <div v-for="subtab in section.subtabs" :key="subtab.key" class="border-top">
              <div class="subtab-header d-flex align-items-start justify-content-between">
                <span class="pointer flex-grow-1"
                      @click="toggleOpen(product.app + '/' + section.key + '/' + subtab.key)">
                  <chevron-down-icon v-if="isOpen(product.app + '/' + section.key + '/' + subtab.key)" class="icon-sm"/>
                  <chevron-right-icon v-else class="icon-sm"/>
                  <strong class="subtab-label">{{ subtab.label }}</strong>
                  <span class="badge badge-light ml-2">
                    {{ marked(ids(product.app, subtab.items)) }}/{{ subtab.items.length }}
                  </span>
                </span>
                <span v-if="!disabled" class="text-nowrap">
                  <a class="small pointer" @click="toggleMany(ids(product.app, subtab.items), true)">Tudo</a>
                  <span class="text-muted mx-1">|</span>
                  <a class="small text-muted pointer" @click="toggleMany(ids(product.app, subtab.items), false)">Nada</a>
                </span>
              </div>

              <permission-item-list
                  v-if="isOpen(product.app + '/' + section.key + '/' + subtab.key)"
                  :app="product.app"
                  :dimensions="catalog.scope_dimensions"
                  :disabled="disabled"
                  :indented="true"
                  :items="subtab.items"
                  :scopes="scopes"
                  :section="section.key"
                  :selected="selected"
                  :subtab="subtab.key"
                  @toggle="onToggle"
                  @scope-clear="onScopeClear"
                  @scope-toggle="onScopeToggle">
                <template #companies="{ dimension }">
                  <company-scope
                      :all-companies="allCompanies"
                      :disabled="disabled"
                      :hint="dimension.hint"
                      :label="dimension.label"
                      :selected-cnpjs="cnpjs"
                      @all-companies="$emit('all-companies', $event)"
                      @cnpjs="$emit('cnpjs', $event)"/>
                </template>
              </permission-item-list>
            </div>

          </template>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import CompanyScope from './CompanyScope'
  import PermissionItemList from './PermissionItemList'
  import { ChevronDownIcon, ChevronRightIcon, SearchIcon } from 'vue-feather-icons'
  import { permissionId, productItems, sectionItems } from '@/models/permissionCatalog'

  export default {
    components: { ChevronDownIcon, ChevronRightIcon, CompanyScope, PermissionItemList, SearchIcon },
    computed: {
      // A API já devolve só os produtos contratados — aqui é só a busca.
      products() {
        const term = this.search.trim().toLowerCase()
        if (!term) return this.catalog.products

        const keep = (item, productLabel) =>
          item.label.toLowerCase().includes(term) ||
          (item.hint || '').toLowerCase().includes(term) ||
          productLabel.toLowerCase().includes(term)

        return this.catalog.products
          .map(product => Object.assign({}, product, {
            sections: product.sections
              .map(section => Object.assign({}, section, {
                items: (section.items || []).filter(item => keep(item, product.label)),
                subtabs: (section.subtabs || [])
                  .map(subtab => Object.assign({}, subtab, {
                    items: subtab.items.filter(item => keep(item, product.label))
                  }))
                  .filter(subtab => subtab.items.length > 0)
              }))
              .filter(section => section.items.length > 0 || section.subtabs.length > 0)
          }))
          .filter(product => product.sections.length > 0)
      },
      searching() {
        return this.search.trim().length > 0
      }
    },
    data: () => ({
      // Tudo recolhido: produto, aba e sub-aba. Com 7 produtos, abrir tudo de
      // início vira rolagem infinita.
      expanded: [],
      search: ''
    }),
    methods: {
      ids(app, items) {
        return items.map(item => permissionId(app, item.key))
      },
      isOpen(key) {
        return this.searching || this.expanded.includes(key)
      },
      items(section) {
        return sectionItems(section)
      },
      marked(ids) {
        return ids.filter(id => this.selected.includes(id)).length
      },
      onScopeClear(app, dimension) {
        this.$emit('scope-clear', app, dimension)
      },
      onScopeToggle(app, dimension, value) {
        this.$emit('scope-toggle', app, dimension, value)
      },
      onToggle(id, checked) {
        this.$emit('toggle', id, checked)
      },
      productIds(product) {
        return this.ids(product.app, productItems(product))
      },
      toggleMany(ids, checked) {
        this.$emit('toggle-many', ids, checked)
      },
      toggleOpen(key) {
        const at = this.expanded.indexOf(key)
        if (at >= 0) this.expanded.splice(at, 1)
        else this.expanded.push(key)
      }
    },
    name: 'PermissionPicker',
    props: {
      allCompanies: { default: true },
      catalog: { required: true },
      cnpjs: { default: () => [] },
      disabled: { default: false },
      scopes: { default: () => ({}) },
      selected: { default: () => [] }
    }
  }
</script>

<style scoped>
  .pointer { cursor: pointer; }
  .icon-sm { width: 14px; height: 14px; }
  .section-header { background: rgba(0, 0, 0, .02); padding: .35rem .75rem; }
  .subtab-header { padding: .35rem .75rem .35rem 1.75rem; }
  .subtab-label { font-size: 15px; }
  .position-relative { position: relative; }
</style>
